<template>
  <div
    @click="value > 0 ? $emit('open-modal') : null"
    class="group flex flex-col sm:flex-col items-start h-auto bg-white rounded-2xl border border-1 shadow-xl transition-all duration-300"
    :class="{
      'opacity-90': value === 0,
      'cursor-pointer': value > 0,
      'cursor-not-allowed': value === 0,
    }"
  >
    <!-- LAYOUT MOBILE: riga orizzontale immagine + contenuto -->
    <div class="flex sm:hidden w-full items-stretch">
      <!-- Immagine piccola quadrata -->
      <!-- Wrapper esterno: solo padding + border mobile, NO overflow-hidden -->
      <div
        class="relative flex-shrink-0 p-2 sm:p-0 h-100 w-20 sm:w-full h-20 sm:h-auto border-slate-200 rounded-l-2xl"
      >
        <!-- Wrapper interno: clippa immagine + overlay insieme -->
        <div
          class="relative w-full h-full overflow-hidden rounded-xl sm:rounded-none"
        >
          <img
            :src="image"
            class="w-full h-auto rounded-xl sm:rounded-none object-contain sm:object-cover transition-all duration-300"
            :class="{
              'drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]': isFallback,
              'grayscale brightness-75 opacity-60': value === 0,
            }"
            @error="$emit('img-error', $event)"
          />

          <!-- Overlay: ora usa inset-0 perché è relativo al wrapper interno -->
          <div
            class="absolute inset-0 transition-opacity flex items-center justify-center pointer-events-none"
            :class="
              value > 0
                ? 'bg-indigo-900/20 opacity-0 group-hover:opacity-100'
                : 'bg-slate-900/50 opacity-100'
            "
          >
            <i v-if="value > 0" class="fa-solid fa-eye text-white text-lg"></i>

            <div v-else class="flex flex-col items-center gap-0.5">
              <i
                class="fa-solid fa-plug-circle-xmark text-white/70 text-sm"
              ></i>
              <span
                class="text-white text-[8px] font-bold uppercase tracking-widest bg-black/20 px-1.5 py-0.5 rounded"
              >
                Offline
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Contenuto testuale -->
      <div class="flex flex-col justify-center gap-1 p-2 min-w-0 flex-1">
        <span class="font-bold text-slate-700 capitalize truncate text-sm">{{
          title
        }}</span>

        <!-- Badge players + skins -->
        <div class="flex items-center gap-1 flex-wrap">
          <div
            class="group/tooltip relative flex justify-center"
            v-if="value > 0"
          >
            <span
              class="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1"
            >
              <i class="fa-solid fa-user text-[8px]"></i>{{ value }}
            </span>
          </div>
          <div
            class="group/tooltip relative flex justify-center"
            v-if="secondValue > 0"
          >
            <span
              class="bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1"
            >
              <i class="fa-solid fa-globe text-[8px]"></i>{{ secondValue }}
            </span>
          </div>
        </div>

        <!-- Tags -->
        <div v-if="tags !== null">
          <div v-if="tags.length > 0" class="flex flex-wrap gap-1">
            <div
              v-for="tag in displayedTags"
              :key="tag.key || tag.label"
              class="flex items-center rounded-full border bg-slate-50 border-indigo-100 shadow-sm py-0.5 px-2 min-w-0"
            >
              <span class="text-[10px] text-indigo-600 font-bold mr-1">{{
                tag.value
              }}</span>
              <span
                class="text-[9px] text-slate-500 font-medium truncate max-w-[60px]"
                >{{ tag.label }}</span
              >
            </div>
            <div
              v-if="remainingCount > 0"
              class="flex items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5"
            >
              <span class="text-[9px] text-indigo-700 font-bold"
                >+{{ remainingCount }}</span
              >
            </div>
          </div>
          <div v-else class="text-[11px] text-slate-800">
            No activity yet!
          </div>
        </div>
      </div>
    </div>

    <!-- LAYOUT DESKTOP: layout originale (visibile da sm in su) -->
    <div class="hidden sm:flex h-100 flex-col w-full">
      <div
        class="flex items-center justify-between w-full border-b p-2 bg-slate-50/50 rounded-t-2xl"
      >
        <span
          class="font-bold text-slate-700 capitalize flex-1 truncate text-sm"
          >{{ title }}</span
        >
        <div class="flex items-center gap-1">
          <div
            class="group/tooltip relative flex justify-center"
            v-if="value > 0"
          >
            <span
              class="bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1 cursor-help"
            >
              <i class="fa-solid fa-user text-[8px]"></i>{{ value }}
            </span>
            <span
              class="absolute bottom-full mb-2 hidden group-hover/tooltip:block w-auto px-2 py-1 text-[10px] text-white bg-slate-800 rounded shadow-lg whitespace-nowrap z-10"
            >
              Players Live
              <span
                class="absolute top-full left-1/2 -ml-1 border-[4px] border-transparent border-t-slate-800"
              ></span>
            </span>
          </div>
          <div
            class="group/tooltip relative flex justify-center"
            v-if="secondValue > 0"
          >
            <span
              class="bg-blue-500 text-white text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1 cursor-help"
            >
              <i class="fa-solid fa-globe text-[8px]"></i>{{ secondValue }}
            </span>
            <span
              class="absolute bottom-full mb-2 hidden group-hover/tooltip:block w-auto px-2 py-1 text-[10px] text-white bg-slate-800 rounded shadow-lg whitespace-nowrap z-10"
            >
              {{ secondValue }} Skins
              <span
                class="absolute top-full left-1/2 -ml-1 border-[4px] border-transparent border-t-slate-800"
              ></span>
            </span>
          </div>
        </div>
      </div>

      <div class="relative h-full w-full">
        <img
          :src="image"
          class="w-full h-full mx-auto transition-all duration-300 object-cover hover:scale-[1.02]"
          :class="{
            'drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]': isFallback,
            'opacity-80': value === 0,
            'rounded-b-2xl': tags === null,
          }"
          @error="$emit('img-error', $event)"
        />
        <div
          class="absolute inset-0 transition-opacity flex items-center justify-center backdrop-blur-sm"
          :class="[
            value > 0
              ? 'bg-indigo-900/20 opacity-0 group-hover:opacity-100'
              : 'bg-slate-900/50 opacity-100',
            { 'rounded-b-2xl': tags === null },
          ]"
        >
          <i v-if="value > 0" class="fa-solid fa-eye text-white text-3xl"></i>
          <div v-else class="flex flex-col items-center gap-1">
            <i class="fa-solid fa-plug-circle-xmark text-white text-xl"></i>
            <span
              class="text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
            >
              No Players Live
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="tags !== null"
        class="px-2 my-2 h-100 w-full items-center justify-start flex"
      >
        <div
          v-if="tags.length > 0"
          class="flex flex-wrap gap-1.5 justify-start"
        >
          <div
            v-for="tag in displayedTags"
            :key="tag.key || tag.label"
            class="flex items-center rounded-full border bg-slate-50 border-indigo-100 shadow-sm py-0.5 px-2 min-w-0"
          >
            <span class="text-[10px] text-indigo-600 font-bold mr-1">{{
              tag.value
            }}</span>
            <span
              class="text-[9px] text-slate-500 font-medium truncate max-w-[60px]"
              >{{ tag.label }}</span
            >
          </div>
          <div
            v-if="remainingCount > 0"
            class="flex items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 shadow-sm cursor-pointer hover:bg-indigo-100 transition-colors"
            :title="`Show other ${remainingCount} skins`"
          >
            <span class="text-[9px] text-indigo-700 font-bold"
              >+{{ remainingCount }}</span
            >
          </div>
        </div>
        <div
          v-else
          class="w-full text-[12px] text-slate-800 text-center"
        >
          No activity yet!
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ActivityCard",
  props: {
    title: { type: String, required: true },
    value: { type: Number, default: 0 },
    secondValue: { type: Number, default: 0 },
    tags: { type: Array, default: null },
    image: { type: String, required: true },
    isFallback: { type: Boolean, default: false },
  },
  computed: {
    displayedTags() {
      return this.tags.slice(0, 3);
    },
    remainingCount() {
      return Math.max(0, this.tags.length - 3);
    },
  },
  emits: ["open-modal", "img-error"],
};
</script>
